<template>
	<section class="products" :style="`--no-image-text:  '${$t('shoplist.noImage')}';`" v-htmlclass="customHtmlClasses">
		<ShopMenuHeader
			v-if="menuMode"
			class="menu-header--mobile insicht insicht--fade-up insicht--fast"
			:title="menuLocationName"
			:subtitle="$t('shoplist.menu.title')"
		/>
		<div class="products__wrapper">
			<div class="products__categories insicht insicht--fade-up insicht--fast" ref="categories">
				<ListScrollPane
					class="products__categories-wrapper"
					:class="{
						'products__categories-wrapper--main': showSubCategories
					}"
					v-slot="{canScroll, scrolledTop, scrolledBottom, scrolled}"
					:to-watch="categories"
				>
					<ShoplistFilter
						:filters="filters"
						:filtersActive="filtersActive"
						@apply="onFilterApply"
					/>

					<template>
						<div
							class="products__categories-group-overlap products__categories-group-overlap--before"
							data-scrollcontainer-up
							:class="{
								'products__categories-group-overlap--visible': canScroll && !scrolledTop,
								'products__categories-group-overlap--scrolled': scrolled,
							}"
						/>
						<div
							class="products__categories-group"
							ref="categoryGroup"
							data-scrollcontainer
						>
							<ul class="products__categories-list">
								<!-- TODO: this should be the default category for a user that is logged in. -->
								<!-- FIXME: when this category is active and the page is reloaded, the wrong category is highlighted
								and you can't click on this one, because the hash is still set to favorite. -->

								<ShoplistCategory
									v-if="showPersonalStartpage"
									ref="categoryLink"
									:category="{
										svgIconKey: 'category-favorites',
										name: $t('shoplist.favorites.title')
									}"
									:active="showFavorites"
									data-scrollcontainer-item
									@click="onFavoriteProducts"
								/>

								<!-- loop -->
								<ShoplistCategory
									ref="categoryLink"
									v-for="category in categoriesFiltered"
									:key="category.code"
									:category="category"
									:active="showFavorites ? false : currentScrollCategory && !showSubCategories ? currentScrollCategory == category.code : activeCategory == category.code"
									data-scrollcontainer-item
									@click="onChangeCategory(category)"
								/>
								<!-- loop end -->
							</ul>
						</div>
						<div
							class="products__categories-group-overlap products__categories-group-overlap--after"
							data-scrollcontainer-down
							:class="{
								'products__categories-group-overlap--visible': canScroll && !scrolledBottom,
								'products__categories-group-overlap--scrolled': scrolled,
							}"
						/>
					</template>
				</ListScrollPane>

				<ListScrollPane
          v-if="showSubCategories"
					class="products__categories-wrapper products__categories-wrapper--subcategories"
					v-slot="{canScroll, scrolledTop, scrolledBottom, scrolled}"
					:to-watch="currentSubCategories"
				>
					<template>
						<div
							class="products__categories-group-overlap products__categories-group-overlap--before products__categories-group-overlap--subcategory"
							data-scrollcontainer-up
							:class="{
								'products__categories-group-overlap--visible': canScroll && !scrolledTop,
								'products__categories-group-overlap--scrolled': scrolled,
							}"
						/>
						<div
							class="products__categories-group"
							ref="subCategoryGroup"
							data-scrollcontainer
						>
							<ul class="products__categories-list">
								<!-- loop -->
								<ShoplistCategory
									ref="categoryLink"
									v-for="category in currentSubCategories"
									:key="category.code"
									:category="category"
									:active="showFavorites ? false : currentScrollCategory ? currentScrollCategory == category.code : activeSubCategory == category.code"
									modifier="subcategory"
									data-scrollcontainer-item
									@click="onChangeSubCategory(category)"
								/>
								<!-- loop end -->
							</ul>
						</div>
						<div
							class="products__categories-group-overlap products__categories-group-overlap--after products__categories-group-overlap--subcategory"
							data-scrollcontainer-down
							:class="{
								'products__categories-group-overlap--visible': canScroll && !scrolledBottom,
								'products__categories-group-overlap--scrolled': scrolled,
							}"
						/>
					</template>
				</ListScrollPane>

			</div>
			<div
				class="products__inner insicht insicht--fade-in insicht--fast"
				:class="{
					'products__inner--public': (publicMode || !hasBasket) && !menuMode,
					'products__inner--menu': menuMode,
				}"
			>

				<ShopHeader v-if="hasBasket && !publicMode && !menuMode"/>

				<ShopMenuHeader
					v-if="menuMode"
					:title="menuLocationName"
					:subtitle="$t('shoplist.menu.title')"
				/>

        <GeneralMessage
          v-if="cartGlobalMessage"
          :message="cartGlobalMessage"
          type="warning"
          stick
        />

        <GeneralMessage
          v-for="(warning, i) in cartWarnings"
          :key="`${warning.code}${i}`"
          :message="warning.message"
          type="warning"
          stick
        />

				<div
					v-if="hasFeature('FEATURE_GROUP_ORDERS') && cartIsGroupOrder && groupOrderIsOwner"
					class="group-order-share"
				>
					<div
						class="group-order-share__text"
						v-html="$t('grouporder.share.text')"
					/>
					<div class="group-order-share__link">
						<div
							class="group-order-share__link-text"
							v-html="groupOrderJoinUrl"
						/>
						<div
							v-if="copyGroupOrderUrlCopied"
							class="group-order-share__link-success"
							v-html="$t('grouporder.share.copied')"
						/>
						<button
							class="group-order-share__link-button"
							type="button"
							@click.prevent="onCopyGroupOrderUrl(groupOrderJoinUrl)"
						>
							<SVGDuplicate class="group-order-share__link-button-icon"/>
						</button>
						<button
							class="group-order-share__link-button"
							type="button"
							@click.prevent="onGroupOrderQR"
						>
							<SVGQrCode class="group-order-share__link-button-icon"/>
						</button>
					</div>
				</div>

				<div
					v-if="hasBasket && isLoggedIn && lastOrdersFiltered.length && !showFavorites && !publicMode && !menuMode"
					class="last-orders-teaser"
				>
					<SVGBag class="last-orders-teaser__icon"/>
					<div class="last-orders-teaser__content">
						<div
							class="last-orders-teaser__text"
							v-html="$t('shoplist.lastOrders.teaserText')"
						/>
						<Button
							class="last-orders-teaser__button"
							level="tertiary"
							:text="$t('shoplist.lastOrders.teaserButton')"
							type="button"
							arrow
							@click.prevent="onFavoriteProducts"
						/>
					</div>
				</div>

				<LastOrders
					v-if="hasBasket && isLoggedIn && showFavorites && !publicMode && !menuMode"
					:orders="lastOrdersFiltered"
					class="products__last-orders"
					@click="onLastOrderClick"
				/>

        <template v-if="isLoggedIn && productsCustomerExclusive.length && showFavorites && !menuMode">
          <h2
            class="products__title"
            v-html="$t('shoplist.favorites.exclusiveTitle')"
          />
          <div
            class="products__text"
            v-html="$t('shoplist.favorites.exclusiveText')"
          />
          <div
            v-if="!productsLoading"
            class="products__grid"
            :class="{
              'products__grid--public': !hasBasket && !menuMode,
              'products__grid--menu': menuMode,
            }"
          >
            <ShoplistProduct
              v-for="(product, index) in productsCustomerExclusive"
              :key="product.code"
              :product="product"
              :locked="lockedProducts[product.code]"
              :disabled="cartIsGroupOrderLocked || groupOrderParticipantReady"
              @addToCart="onAddToCart"
              @config="onConfig"
              @click="onClickProduct"
              @orderbox="onOrderboxOpen"
            />
          </div>
        </template >

				<h2
					class="products__title"
					v-if="isLoggedIn && favoriteProducts.length && showFavorites && !publicMode && !menuMode"
					v-html="$t('shoplist.favorites.title')"
				/>

				<Loader
					class="products__loader"
					:show="productsLoading && !loading"
				/>

				<div
					class="products__grid"
					:class="{
						'products__grid--public': (publicMode || !hasBasket) && !menuMode,
						'products__grid--menu': menuMode,
					}"
					v-if="productsEnhanced && !productsLoading"
				>
					<div
						class="products__grid-empty"
						v-if="productsInitialized && !productsEnhanced.length"
						v-html="$t('shoplist.noProducts')"
					/>
					<template v-if="!showFavorites && (currentCategoryIsOverview || showOverviewCategory)">
						<div
							v-for="category in productsEnhancedCategories"
							:key="category.category.code"
							class="products__category products__grid"
							:class="{
								'products__grid--public': (publicMode || !hasBasket) && !menuMode,
								'products__grid--menu': menuMode,
							}"
						>
							<ShoplistCategoryHeader
								ref="categoryHeader"
								:category="category.category"
							/>
							<!-- loop -->
							<ShoplistProduct
								v-for="(product, index) in category.products"
								:key="category.category.code + product.code"
								:product="product"
								:locked="lockedProducts[`${category.category.code}-${product.code}`]"
								:disabled="cartIsGroupOrderLocked || groupOrderParticipantReady"
								@addToCart="onAddToCart($event, category.category.code)"
								@config="onConfig"
								@click="onClickProduct($event, category.category.code)"
								@orderbox="onOrderboxOpen"
							/>
							<template v-if="category.children && category.children.length">
								<div
									v-for="(child, i) in category.children"
									class="products__category-child products__grid"
                  :class="{
                    'products__grid--public': (publicMode || !hasBasket) && !menuMode,
                    'products__grid--menu': menuMode,
                  }"
								>
									<h3 class="products__category-child-header">
										<component
											v-if="iconComponent(child.category.svgIconKey)"
											:is="iconComponent(child.category.svgIconKey)"
											class="products__category-child-header-icon"
										/>
										<SVGInfo
											v-else
											class="products__category-child-header-icon"
										/>

										<h2
											class="products__category-child-header-title"
											v-html="child.category.name"
										/>
									</h3>
									<ShoplistProduct
										v-for="(product, index) in child.products"
										:key="child.category.code + product.code"
										:product="product"
										:locked="lockedProducts[`${child.category.code}-${product.code}`]"
										:disabled="cartIsGroupOrderLocked || groupOrderParticipantReady"
										@addToCart="onAddToCart($event, child.category.code)"
										@config="onConfig"
										@click="onClickProduct($event, child.category.code)"
										@orderbox="onOrderboxOpen"
									/>
								</div>
							</template>
							<!-- loop end -->
						</div>
					</template>
					<template v-else>
						<!-- loop -->
						<ShoplistProduct
							v-for="(product, index) in productsEnhancedLimited"
							:key="product.code"
							:product="product"
							:locked="lockedProducts[product.code]"
							:disabled="cartIsGroupOrderLocked || groupOrderParticipantReady"
							@addToCart="onAddToCart"
							@config="onConfig"
							@click="onClickProduct"
							@orderbox="onOrderboxOpen"
						/>
						<!-- loop end -->
					</template>
				</div>
			</div>
			<div
				class="products__cart-placeholder"
				v-if="hasBasket && !publicMode && !menuMode"
			>
				<OverlaySidebarCart
					class="insicht insicht--fade-up insicht--fast"
					ref="sidebarcart"
					is-inline
					:locked-products="lockedProducts"
					@addToCart="onAddToCart"
					@config="onConfig"
				/>
			</div>
		</div>

		<MobileStickyCart v-if="hasBasket && !publicMode && !menuMode"/>
		<Modal
			overlay-type="product-detail"
			ref="modal"
			@close="onModalProductClose"
		>
			<OverlayProduct
				v-if="modalProductCode"
				:code="modalProductCode"
				:product-data="modalProduct"
				:locked="lockedProducts[modalProductCategory ? `${modalProductCategory}-${modalProductCode}` : modalProductCode]"
				:disabled="cartIsGroupOrderLocked || groupOrderParticipantReady"
				@addToCart="onAddToCart($event, modalProductCategory)"
				@config="onConfig"
				@click="onClickProduct"
				@orderbox="onOrderboxOpen"
				is-shop
			/>
		</Modal>
		<Modal
			v-if="hasFeature('FEATURE_GROUP_ORDERS') && cartIsGroupOrder"
			overlay-type="group-order-qr"
			ref="groupOrderQR"
			narrow
		>
			<div class="overlay-dialog">
				<h2
					class="overlay-dialog__title"
					v-html="$t('grouporder.share.qrTitle')"
				/>
				<div
					class="overlay-dialog__text"
					v-html="$t('grouporder.share.qrText')"
				/>
				<div class="group-order-share">
					<div class="group-order-share__link">
						<div
							class="group-order-share__link-text"
							v-html="groupOrderJoinUrl"
						/>
						<div
							v-if="copyGroupOrderUrlCopied"
							class="group-order-share__link-success"
							v-html="$t('grouporder.share.copied')"
						/>
						<button
							class="group-order-share__link-button"
							type="button"
							@click.prevent="onCopyGroupOrderUrl(groupOrderJoinUrl)"
						>
							<SVGDuplicate class="group-order-share__link-button-icon"/>
						</button>
					</div>
				</div>
				<QRCode
					:content="groupOrderJoinUrl"
				/>
			</div>
		</Modal>
		<OverlayError/>
		<OverlayChildProducts
			v-if="hasFeature('FEATURE_CHILDPRODUCTS') && hasBasket && !publicMode && !menuMode"
			ref="childproductsOverlay"
			@edit="onChildProductsEdit"
			@add="onChildProductsAdd"
		/>
	</section>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import urlWatcher from '@/helpers/urlwatcher'
import GenericMixin from '@/mixins/GenericVueMixin'
import OverlaySidebarCart from '@/pages/OverlaySidebarCart'
import OverlayError from '@/pages/OverlayError';
import ShoplistFilter from '@/components/ShoplistFilter'
import ShoplistProduct from '@/components/ShoplistProduct'
import ShoplistCategory from '@/components/ShoplistCategory'
import ShoplistCategoryHeader from '@/components/ShoplistCategoryHeader'
import OverlayProduct from '@/pages/OverlayProduct'
import OverlayChildProducts from '@/pages/OverlayChildProducts'
import MobileStickyCart from '@/components/MobileStickyCart'
import Button from '@/components/Button'
import Modal from '@/components/Modal'
import ListScrollPane from '@/components/ListScrollPane'
import Loader from '@/components/Loader';
import LastOrders from '@/components/LastOrders';
import ShopHeader from '@/components/ShopHeader';
import ShopMenuHeader from '@/components/ShopMenuHeader';
import QRCode from '@/components/QRCode'
import htmlComponent from "htmlcomponent"
import {eventhub, EVENT_CART_TIMECHANGED, EVENT_SHOP_INITIALIZED, EVENT_CART_CREATED} from '@/store/eventhub'
import scrrroll from 'scrrroll';
import { supportsPassive } from '@/utils/features'
import { getUrlParams, makeUrl } from '@/utils/urlParams'
import { dayjs } from '@/helpers/dayjs'
import GeneralMessage from '@/components/GeneralMessage.vue'

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		OverlaySidebarCart,
		OverlayError,
		ShoplistFilter,
		ShoplistProduct,
		ShoplistCategory,
		ShoplistCategoryHeader,
		OverlayProduct,
		OverlayChildProducts,
		MobileStickyCart,
		Button,
		Modal,
		ListScrollPane,
		Loader,
		LastOrders,
		ShopHeader,
		ShopMenuHeader,
		QRCode,
    GeneralMessage,
	},
	data() {
		return {
			shopInitialized: false,
			lockedProducts: {},
			showFavorites: false,
			modalProduct: null,
			modalProductCode: null,
			modalProductCategory: null,
			categoryScrollStart: false,
			categoryScrollEnd: false,
			productLoadThresholdMin: 60,
			productLoadThresholdProgression: 5,
			productLoadThresholdMax: 90,
      productsPerPage: 16,
      productPagesLoaded: 1,
			currentScrollCategory: null,
			copyGroupOrderUrlTimeout: null,
			copyGroupOrderUrlCopied: false,

			menuMode: !!this.$root.options.menuMode,
			menuChannel: this.$root.options.menuChannel,
			menuLocationName: this.$root.options.menuLocationName,
			menuTimedAvailability: this.$root.options.menuTimedAvailability,
			reloadTimeout: 0,
		}
	},
	computed: {
		...mapGetters("productlist", [
			"categories",
			"categoriesFiltered",
			"hasSubCategories",
			"currentCategories",
      "showOverviewCategory",
      "currentCategorySiblings",
			"currentCategoryIsOverview",
			"currentCategoriesHaveOverview",
			"currentOverviewCategory",
			"currentOverviewCategoryIsTop",
			"filters",
			"filtersActive",
			"currentTaxon",
			"currentTaxonPath",
			"currentSubCategories",
			"favoriteCode",
			"productsLoading",
			"productsInitialized",
			"nextProductAvailablityChangeTime",
      "useMenu",
      "publicMode",
      "productsEnhancedCategories",
      "productsEnhanced",
      "productsEnhancedTaxon",
      "productsCustomerExclusive",
		]),
		...mapGetters("account", [
			"isLoggedIn",
			"lastOrders",
			"favoriteProducts",
      "favoriteProductsEnhanced",
			"accountUserDetail"
		]),
		...mapGetters("cart", [
			"shippingMethod",
			'hasBasket',
			'cartIsGroupOrder',
			'cartIsGroupOrderLocked',
      'cartGlobalMessage',
      'tokenValue',
      'cartWarnings',
		]),
		...mapGetters("grouporder", [
			'groupOrderParticipantReady',
			'groupOrderIsOwner',
			'groupOrderJoinUrl',
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		...mapGetters([
			'initialized',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		customHtmlClasses() {
			return {
				'shop--public': (this.publicMode || !this.hasBasket) && !this.menuMode,
				'shop--menu': this.menuMode,
			}
		},
		activeCategory() {
			return this.showFavorites ? this.favoriteCode : this.currentTaxonPath[0] && this.currentTaxonPath[0].code
		},
		activeSubCategory() {
			return this.currentTaxonPath.length > 1 ? this.currentTaxonPath[1].code : ""
		},
		showSubCategories() {
			return this.currentSubCategories.length > 0
		},
		showPersonalStartpage() {
			return !!(this.hasBasket && this.isLoggedIn && (this.favoriteProducts.length || this.lastOrdersFiltered.length || this.productsCustomerExclusive.length) && !this.publicMode && !this.menuMode);
		},
		productsLimit() {
			if (this.currentCategoryIsOverview || this.showOverviewCategory) {
				return this.productPagesMax * this.productsPerPage; // TODO: test performance with large category, specially on mobile
			}

			return this.productPagesLoaded * this.productsPerPage;
    },
		productPagesMax() {
			let productsLength

      if (this.useMenu) {
        productsLength = this.productsEnhancedTaxon.length;
      } else {
        productsLength = this.productsEnhanced.length;
      }

			if (!this.showFavorites && (this.currentCategoryIsOverview || this.showOverviewCategory)) {
				productsLength = this.productsEnhancedCategories.reduce((p, c) => c.products.length + p, 0);
			}

			return Math.ceil(productsLength / this.productsPerPage)
		},
		productLoadThreshold() {
			return Math.min(this.productLoadThresholdMax, this.productLoadThresholdMin + (this.productLoadThresholdProgression * (this.productPagesLoaded - 1)));
		},
		productsEnhancedLimited() {
			return (
					this.showFavorites
					? this.favoriteProductsEnhanced
					: (this.useMenu
						? this.productsEnhancedTaxon
						: this.productsEnhanced)
					).slice(0, this.productsLimit)
		},
		lastOrdersFiltered() {
			return this.lastOrders
				.filter(order => order.shipmentCode === this.shippingMethod.code)
				.slice(0,3);
		},
		productsEnhancedCategoriesLimited () {
			let categories = this.productsEnhancedCategories
			const limit = this.productsLimit

			let count = 0;
			let limitedCategories = categories.reduce((p, {category, products}) => {
				if ((count + 1) <= limit) {
					// limit products
					let newCount = count + products.length
					if (newCount > limit) {
						newCount = limit
						products = products.slice(0, limit - count)
					}
					p.push({
						category,
						products,
					})
					count = newCount
				}
				return p;
			}, []);

			return limitedCategories;
		},
	},
	watch: {
		currentScrollCategory(newValue, oldValue) {
			if (newValue !== oldValue && this.$refs.categoryLink?.length) {
				const activeCategoryLink = [...this.$refs.categoryLink].find(c => c.category.code == newValue);
				if (activeCategoryLink) {
					activeCategoryLink.$parent.scrollTo(activeCategoryLink.$el);
				}
			}
		}
	},
	methods: {
		async onCopyGroupOrderUrl(value) {
			try {
				await navigator.clipboard.writeText(value)
				this.copyGroupOrderUrlCopied = true
				clearTimeout(this.copyGroupOrderUrlTimeout)
				this.copyGroupOrderUrlTimeout = setTimeout(() => {
					this.copyGroupOrderUrlCopied = false
				}, 2000)
			}
			catch (err) {
				this.isDebug() && console.error(err)
			}
		},
		onGroupOrderQR() {
			this.$refs.groupOrderQR?.open()
		},
		onClickProduct(code, categoryCode = null) {
			this.modalProductCategory = categoryCode;
			urlWatcher.change({taxon: this.currentScrollCategory || this.currentTaxon, product: code});
		},
		onModalProductOpen(code) {
			this.modalProduct = this.getProduct(code);
			this.modalProductCode = code;
			if (this.modalProductCode) {
				this.$refs.modal.open();
			}
		},
		onModalProductClose() {
			urlWatcher.change({product: null});
			this.modalProduct = null;
			this.modalProductCode = null;
			this.modalProductCategory = null;
		},
		onOrderboxOpen() {
			htmlComponent.send('overlay:open', {type: 'overlay-orderbox'});
		},
		onOrderboxClose() {
			htmlComponent.send('overlay:close', {type: 'overlay-orderbox'});
		},
		async onConfig(product) {
			htmlComponent.send('overlay:open', {type: 'overlay-childproducts', product, selectedCodes: product.defaultChildProducts});
		},
		async onAddToCart(item, categoryCode = null) {
			const lockKey = categoryCode ? `${categoryCode}-${item.productCode}` : item.productCode;
			Vue.set(this.lockedProducts, lockKey, true);
			try {
				const lastItem = await this.$store.dispatch('cart/addItems', {items: [item]})
				// HACK: we currently inform the sidebarcart about the add. This logic has to be changed in the future
				if (lastItem) {
					// currently we get only an item if its really an add, not just a quantity change
					this.$refs.sidebarcart.onNotifyProductAdded(lastItem)
				}
			}
			catch(err) {
				this.handleErrorDialog(err, {
					title: err?.response?.data?.errors?.availability ? this.$t('shoplist.product.errors.availability') : this.$t('forms.errors.error'),
					message: err?.response?.data?.errors?.availability?.join('<br>') || this.$t('forms.errors.generic'),
					confirm: 'OK',
          callback: () => this.onAddToCart(item, categoryCode)
				});
			}
			Vue.set(this.lockedProducts, lockKey, false);
		},
		async onChangeCategory(category) {
			this.showFavorites = false;
			this.productPagesLoaded = 1;
			let toCat = category.code

			// instead switch to first child
			if (
        (this.useMenu
          ? !this.showOverviewCategory
          : (
            !this.currentOverviewCategoryIsTop ||
            (
              !this.currentCategoriesHaveOverview &&
              !this.currentCategoryIsOverview
            )
          )) &&
				category &&
				category.children &&
				category.children.length > 0
			) {
				toCat = category.children[0].code
			}
      await this.$nextTick()
			urlWatcher.change({"taxon": toCat, product: null})
		},
		onChangeSubCategory(category) {
			urlWatcher.change({"taxon": category.code, product: null})
		},
		onFavoriteProducts() {
			this.productPagesLoaded = 1;
			urlWatcher.change({"taxon": this.favoriteCode, product: null})
		},
		getProduct(code) {
			return this.productsEnhanced.filter(product => product.code == code)[0];
		},
		async onHashChange(params) {
			if (params.taxon &&
				(
					(this.currentScrollCategory && params.taxon != this.currentScrollCategory) ||
					(!this.currentScrollCategory && params.taxon != this.currentTaxon)
				)
			) {
        // this.loading = true
				if (params.taxon == this.favoriteCode) { //TODO: check what happens with logged in vs non
					window.scrollTo(0, 0);
					this.productPagesLoaded = 1;
					this.showFavorites = true
          this.currentScrollCategory = null
					//HACK: this commit should not be here, but its marked for future cleanup
					await this.$store.dispatch('account/loadFavoriteProducts')
          // HACK: favorite Products currently fail on the live system, so we ignore the error
          .catch(err => Promise.resolve("Silent Fail currently"))

				} else {
          this.showFavorites = false

					if (
            (this.useMenu
            ? this.showOverviewCategory
            : (
                this.currentCategoriesHaveOverview &&
						    this.currentCategoryIsOverview
              )
            ) &&
            this.currentCategories.some(c => c.code == params.taxon) ||
            (!this.useMenu && this.currentOverviewCategory?.code == params.taxon)
					) {
						if (this.currentScrollCategory !== params.taxon) {
							const categoryHeader = this.$refs.categoryHeader?.find(c => c.category.code == params.taxon);

							if (categoryHeader) {
                categoryHeader.scrollTo();
							}
              else if (!this.useMenu && params.taxon == this.currentOverviewCategory.code) {
								scrrroll.toTop();
							}
						}
					}
					else {
						window.scrollTo(0, 0);
						this.productPagesLoaded = 1;

						try {
              if (this.useMenu) {
                this.$store.commit('productlist/setActiveCategory', { item: params.taxon })
                if (this.showOverviewCategory) {
                  this.currentScrollCategory = params.taxon
                }
              } else {
                const overviewTaxon = await this.$store.dispatch('productlist/findOverviewTaxon', params.taxon);
                let { publicChannel: channel } = getUrlParams()
                await this.$store.dispatch('productlist/loadProductList', {channel, taxon: overviewTaxon?.code || params.taxon})
                if (overviewTaxon) {
                  this.currentScrollCategory = overviewTaxon.code
                }
              }
						}
						catch(err) {
              this.handleErrorDialog(err, {
                callback: () => this.onHashChange(params)
              })
						}
					}
					// this.loading = false
				}
			}
			if (params.product) {
				this.onModalProductOpen(params.product);
			}
		},
		async onFilterApply(filters = []) {
			this.loading = true;
			for (let {filterId, active} of filters) {
				await this.$store.dispatch('productlist/setFilter', {filterId, active})
			}
			urlWatcher.change({filters: this.filtersActive.join(","), product: null})
			this.loading = false;
		},
		async onLastOrderClick(token) {
			this.loading = true;
			await this.$store.dispatch('redoOrder', { token })
			this.loading = false;
		},
		async onCartTimechanged() {
			this.loading = true
			await this.$store.dispatch('updateShoplist')
			this.loading = false
		},
		async onChildProductsAdd(product, childCodes) {
			this.loading = true;
			try {
				const item = {
						quantity: 1,
						productCode: product.code,
						variantCode: product.selectedVariant,
						childCodes: childCodes,
						isCrossSell: product.isCrossSell,
						forceCopy: true,
				};
				await this.onAddToCart(item);
				this.$refs.childproductsOverlay.close()
			}
			catch(err) {
				this.handleErrorDialog(err, {
          callback: () => this.onChildProductsAddv(product, childCodes)
				});
			}
			this.loading = false;
		},
		async onChildProductsEdit(item, childCodes) {
			this.loading = true;
			try {
				await this.$store.dispatch('childproducts/adjustChildProducts', { item: item, childCodes }, { root: true })
				this.$refs.childproductsOverlay.close()
			}
			catch(err) {
				this.handleErrorDialog(err, {
          callback: () => this.onChildProductsEdit(item, childCodes)
				});
			}
			this.loading = false;
		},
		onContentScroll() {
			if (this.productPagesLoaded < this.productPagesMax) {
				const scrollTop = window.scrollY;
				const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
				const scrollProgress = Math.min(Math.max(((scrollTop / scrollHeight) * 100), 0), 100);

				if (scrollProgress > this.productLoadThreshold) {
					this.productPagesLoaded = Math.min(this.productPagesMax, this.productPagesLoaded + 1);
				}
			}

			if (
        this.useMenu
          ? (
            this.showOverviewCategory &&
            !this.showFavorites
          )
          : (
            this.currentCategoriesHaveOverview &&
            this.currentCategoryIsOverview &&
            !this.showFavorites
          )
      ) {
				let offset = window.headerHeight ?? 0;

				if (this.isMobile()) {
					offset += window.shopCategoriesHeight;
				}

				const current = [...(this.$refs.categoryHeader || [])].reverse().find(header => window.scrollY >= header.$el.offsetTop - offset);

				if (window.scrollY <= 0) {
          if (this.useMenu) {
            this.currentScrollCategory = this.productsEnhancedCategoriesLimited?.at(0)?.category?.code
          } else {
            this.currentScrollCategory = this.currentOverviewCategory.code;
          }
				}
				else {
					this.currentScrollCategory = current?.category?.code;
				}

				if (this.currentScrollCategory) {
					urlWatcher.change({"taxon": this.currentScrollCategory})
				}
			}
			else {
				this.currentScrollCategory = null;
			}
    },
		onShopInitialized() {
			const params = urlWatcher.readUrl()
			this.shopInitialized = true;
			this.onHashChange(params);
			this.onResize();
			this.onContentScroll();
		},
		onResize() {
			if (this.$refs.categories) {
				window.shopCategoriesHeight = this.$refs.categories.clientHeight;
				document.documentElement.style.setProperty('--shop-categories-height', `${window.shopCategoriesHeight}px`);
			}
		},
    onCartCreated() {
      const params = getUrlParams()
      delete params.publicChannel
      history.replaceState({}, '', makeUrl(params));
    },
	},
	async created() {
		this.loading = true
		const urlParams = getUrlParams();
		if (this.menuMode && urlParams.menuChannel) {
			this.menuChannel = urlParams.menuChannel;
		}
    try {
      await this.$store.dispatch('initializeShoplist', { menuMode: this.menuMode, menuChannel: this.menuChannel, menuTimedAvailability: this.menuTimedAvailability })
    } catch(err) {
      if (err.isMaintenanceError) {
        this.onMaintenanceMode()
      }
    }
		this.loading = false

		const params = urlWatcher.readUrl()
		this.onModalProductOpen(params.product);

		// menuMode: we force reload for the next timed menu change (either availabilty change oder next day) but minimum 1 minute
		if (this.menuTimedAvailability) {
			const msToNextChange = Math.max(60000,
				this.nextProductAvailablityChangeTime ?
					this.nextProductAvailablityChangeTime.valueOf() - dayjs().valueOf() :
					dayjs().add(24, 'hour').startOf('day').add(6, 'hour') - dayjs().valueOf()
			)

			this.isDebug() && console.log('menu-store: availability reload in %s hours', msToNextChange / 3600000)
			this.reloadTimeout = setTimeout(() => {
				location.reload()
			}, msToNextChange)
		}

		if (this.cartIsGroupOrder && this.groupOrderIsOwner) {
			this.loading = true
			await this.$store.dispatch('cart/lockGroupOrder', { locked: false })
			this.loading = false
		}
	},
	mounted() {
		urlWatcher.addListener(this.onHashChange)
		eventhub.$on(EVENT_CART_TIMECHANGED, this.onCartTimechanged)
		eventhub.$on(EVENT_SHOP_INITIALIZED, this.onShopInitialized)
    eventhub.$on(EVENT_CART_CREATED, this.onCartCreated)
		window.addEventListener('scroll', this.onContentScroll, supportsPassive ? { passive: false } : false);
		window.addEventListener('resize', this.onResize);
		if (this.shopInitialized) {
			const params = urlWatcher.readUrl()
			this.onHashChange(params);
			this.onResize();
			this.onContentScroll();
		}
	},
	destroyed() {
		urlWatcher.removeListener(this.onHashChange)
		eventhub.$off(EVENT_CART_TIMECHANGED, this.onCartTimechanged)
		eventhub.$off(EVENT_SHOP_INITIALIZED, this.onShopInitialized)
    eventhub.$off(EVENT_CART_CREATED, this.onCartCreated)
		window.removeEventListener('scroll', this.onContentScroll);
		window.removeEventListener('resize', this.onResize);
		clearTimeout(this.reloadTimeout)
	}
};
</script>

<style>
</style>
